import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorHandlingService } from '../error-handling/error-handling.service';
import { HttpHeaderConstants } from '../http-header.constant';
import { SpinnerService } from '../spinner';
import {
    NetsuiteConnection,
    NetsuiteConnectionDependencies,
} from '../../models/appcloud/netsuite/netsuite-connection.model';

@Injectable({
    providedIn: 'root',
})
export class NetsuiteConnectionService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private NETSUITE_CONNECTIONS_URL: string = environment.publicApiHost + '/appcloud/netsuite-connections';

    public findAll(eloquainstallId: number, additionalHeaders: any = {}): Observable<NetsuiteConnection[]> {
        this.#spinnerService.addProcess(
            'NetsuiteConnectionService.findAll.' + eloquainstallId,
            'Loading your connections...'
        );
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('eloquainstallId', eloquainstallId.toString()),
        };
        return this.#http.get<NetsuiteConnection[]>(this.NETSUITE_CONNECTIONS_URL, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving NetSuite Connection data from Relationship One. Please try again later.'
                );
                return of<NetsuiteConnection[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess('NetsuiteConnectionService.findAll.' + eloquainstallId))
        );
    }

    public create(netsuiteConnection: NetsuiteConnection, additionalHeaders: any = {}): Observable<NetsuiteConnection> {
        this.#spinnerService.addProcess('NetsuiteConnectionService.create', 'Creating your NetSuite connection...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };
        return this.#http.post<NetsuiteConnection>(this.NETSUITE_CONNECTIONS_URL, netsuiteConnection, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while creating your NetSuite connection. Please try again later.'
                );
                return of<NetsuiteConnection>();
            }),
            finalize(() => this.#spinnerService.completeProcess('NetsuiteConnectionService.create'))
        );
    }

    public update(
        id: number,
        netsuiteConnection: NetsuiteConnection,
        additionalHeaders: any = {}
    ): Observable<NetsuiteConnection> {
        this.#spinnerService.addProcess('NetsuiteConnectionService.update', 'Updating your NetSuite connection...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.mergePatch, ...additionalHeaders }),
        };
        return this.#http
            .patch<NetsuiteConnection>(this.NETSUITE_CONNECTIONS_URL + '/' + id, netsuiteConnection, httpOptions)
            .pipe(
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while updating your NetSuite connection. Please try again later.'
                    );
                    return of<NetsuiteConnection>();
                }),
                finalize(() => this.#spinnerService.completeProcess('NetsuiteConnectionService.update'))
            );
    }

    public delete(id: number, netsuiteConnection: NetsuiteConnection, additionalHeaders: any = {}): Observable<void> {
        this.#spinnerService.addProcess('NetsuiteConnectionService.delete', 'Deleting your NetSuite connection...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.mergePatch, ...additionalHeaders }),
            body: netsuiteConnection,
        };
        return this.#http.delete<void>(this.NETSUITE_CONNECTIONS_URL + '/' + id, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while deleting your NetSuite connection. Please try again later.'
                );
                return of<void>();
            }),
            finalize(() => this.#spinnerService.completeProcess('NetsuiteConnectionService.delete'))
        );
    }

    public getConnectionDependencies(
        id: number,
        additionalHeaders: any = {}
    ): Observable<NetsuiteConnectionDependencies> {
        this.#spinnerService.addProcess(
            'NetsuiteConnectionService.getConnectionDependencies',
            'Checking for dependencies...'
        );
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };
        return this.#http.get(this.NETSUITE_CONNECTIONS_URL + '/' + id + '/dependencies', httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while checking your NetSuite connection for dependencies. Please try again later.'
                );
                return of<NetsuiteConnectionDependencies>();
            }),
            finalize(() => this.#spinnerService.completeProcess('NetsuiteConnectionService.getConnectionDependencies'))
        );
    }
}
